import React, { useEffect, useState } from 'react';
import { Server, Globe, Shield, Activity, RefreshCw, Plus } from 'lucide-react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { RootState } from '../store';
import { api } from '../services/api';
import MetricCard from '../components/dashboard/MetricCard';
import ProviderStatusCard from '../components/dashboard/ProviderStatus';
import RecentActivity from '../components/dashboard/RecentActivity';
import SystemOverview from '../components/dashboard/SystemOverview';

interface DashboardStats {
  totalProviders: number;
  totalZones: number;
  totalRecords: number;
  healthyProviders: number;
  lastUpdate: string;
}

interface ProviderStatus {
  provider: string;
  status: 'healthy' | 'warning' | 'error';
  zones: number;
  lastCheck: string;
}

const Dashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [stats, setStats] = useState<DashboardStats>({
    totalProviders: 0,
    totalZones: 0,
    totalRecords: 0,
    healthyProviders: 0,
    lastUpdate: new Date().toISOString(),
  });
  const [providerStatuses, setProviderStatuses] = useState<ProviderStatus[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load providers
      const providersResponse = await api.get('/api/v1/dns/providers');
      const providers = providersResponse.data?.providers || [];

      // Load zones
      const zonesResponse = await api.get('/api/v1/dns/zones');
      const zones = zonesResponse.data?.zones || [];

      // Calculate stats
      const totalProviders = providers.length;
      const totalZones = zones.length;
      const totalRecords = zones.reduce(
        (sum: number, zone: any) => sum + (zone.record_count || zone.recordCount || 0),
        0,
      );
      const healthyProviders = providers.filter(
        (p: any) => (p.status || p.health_status) === 'active' || p.health_status === 'healthy',
      ).length;

      setStats({
        totalProviders,
        totalZones,
        totalRecords,
        healthyProviders,
        lastUpdate: new Date().toISOString(),
      });

      // Mock provider statuses for now
      setProviderStatuses(
        providers.map((provider: any) => ({
          provider: provider.name,
          status: provider.status === 'active' ? 'healthy' : 'warning',
          zones: zones.filter(
            (z: any) => z.provider_id === provider.id || z.providerId === provider.id,
          ).length,
          lastCheck: new Date().toISOString(),
        })),
      );
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-fade-in">
        {/* Loading Header */}
        <div className="space-y-3">
          <div className="h-8 w-1/3 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
          <div className="h-4 w-2/3 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        </div>

        {/* Loading Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card p-6">
              <div className="flex items-start space-x-3">
                <div className="w-12 h-12 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                  <div className="h-8 w-1/2 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Loading Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="card">
              <div className="card-header">
                <div className="h-6 w-1/3 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
              </div>
              <div className="card-body space-y-4">
                {Array.from({ length: 3 }).map((_, j) => (
                  <div
                    key={j}
                    className="h-16 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Professional Header */}
      <div className="flex items-center justify-between">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h1 className="text-display-2 text-slate-900 dark:text-white mb-2">
            Welcome back, {user?.name || 'Administrator'}
          </h1>
          <p className="text-body text-slate-600 dark:text-slate-400">
            Here's an overview of your DNS infrastructure and recent activity.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center space-x-3"
        >
          <motion.button
            className="inline-flex items-center justify-center px-4 py-2 text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 font-medium rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={loadDashboardData}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </motion.button>
          <motion.button
            className="inline-flex items-center justify-center px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Provider
          </motion.button>
        </motion.div>
      </div>

      {/* Professional Metrics Grid */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <MetricCard
          title="DNS Providers"
          subtitle="Active connections"
          value={stats.totalProviders}
          icon={Server}
          color="blue"
          trend={{ value: 12, direction: 'up' }}
        />
        <MetricCard
          title="DNS Zones"
          subtitle="Managed domains"
          value={stats.totalZones}
          icon={Globe}
          color="green"
          trend={{ value: 8, direction: 'up' }}
        />
        <MetricCard
          title="DNS Records"
          subtitle="Total entries"
          value={stats.totalRecords.toLocaleString()}
          icon={Shield}
          color="purple"
          trend={{ value: 5, direction: 'up' }}
        />
        <MetricCard
          title="System Health"
          subtitle="Provider status"
          value={`${Math.round((stats.healthyProviders / Math.max(stats.totalProviders, 1)) * 100)}%`}
          icon={Activity}
          color={stats.healthyProviders === stats.totalProviders ? 'green' : 'red'}
          trend={{
            value: stats.healthyProviders === stats.totalProviders ? 0 : 15,
            direction: stats.healthyProviders === stats.totalProviders ? 'neutral' : 'down',
          }}
        />
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProviderStatusCard providerStatuses={providerStatuses} />
        <RecentActivity />
      </div>

      <SystemOverview lastUpdate={stats.lastUpdate} />
    </motion.div>
  );
};

export default Dashboard;
